import i18n from '../i18n.js';
import { calculerFacture } from '@artosera/shared';
import { DEMO_OEUVRES, DEMO_EXPOSITIONS, DEMO_CONTACTS, DEMO_VIGNETTES } from './demoFixtures.js';
import { ApiError } from './errors.js';
import type {
  ApprobationNegociation,
  ChargePresentation,
  CorpsSelection,
  DemandeApprobation,
  ElementSelection,
  EntreeCalcul,
  InteretPresentation,
  ModificationSelection,
  ReglagesNegociation,
  SelectionPresentation,
} from './presentation.js';

const selections: SelectionPresentation[] = [];
const interets = new Map<string, InteretPresentation[]>();
const planchers = new Map<string, number>();
let reglages: ReglagesNegociation = {
  remiseMaxPointsDeBase: 1000,
  parArtiste: [],
  seuilApprobationPointsDeBase: null,
  version: 0,
};
const approbations: ApprobationNegociation[] = [];
let numero = 0;
const maintenant = () => new Date().toISOString();
function conflit() {
  throw new ApiError(409, 'conflit.version', 'Les données ont changé.');
}
function liste<T>(elements: T[], params: URLSearchParams) {
  const page = Number(params.get('page') ?? 1);
  const parPage = Number(params.get('parPage') ?? 50);
  return {
    elements: elements.slice((page - 1) * parPage, page * parPage),
    pagination: {
      page,
      parPage,
      total: elements.length,
      pages: Math.ceil(elements.length / parPage),
    },
  };
}
function creer(corps: CorpsSelection): SelectionPresentation {
  let elements =
    corps.elements?.map((element) => ({
      ...element,
      exemplaireId: element.exemplaireId ?? null,
    })) ?? [];
  let contactId = corps.contactId ?? null;
  let origine: SelectionPresentation['origine'] = { type: 'oeuvres', id: null };
  if (corps.depuis && 'exposition' in corps.depuis) {
    const expositionId = corps.depuis.exposition;
    const exposition = DEMO_EXPOSITIONS.find((exposition) => exposition.id === expositionId);
    elements = (exposition?.oeuvres ?? []).map((oeuvreId) => ({ oeuvreId, exemplaireId: null }));
    origine = { type: 'exposition', id: corps.depuis.exposition };
  }
  if (corps.depuis && 'contact' in corps.depuis) {
    contactId = corps.depuis.contact;
    origine = { type: 'contact', id: contactId };
  }
  const selection: SelectionPresentation = {
    id: `selection-demo-${++numero}`,
    titre: corps.titre,
    contactId,
    note: corps.note ?? '',
    elements,
    origine,
    archivee: false,
    archiveeLe: null,
    version: 0,
    creeeLe: maintenant(),
    modifieeLe: maintenant(),
  };
  selections.unshift(selection);
  return selection;
}
function calculer(corps: EntreeCalcul) {
  const oeuvre = DEMO_OEUVRES.find((oeuvre) => oeuvre.id === corps.oeuvreId);
  if (!oeuvre || oeuvre.prixPublicCentimes === null)
    throw new ApiError(400, 'validation.invalide', 'Prix public requis.');
  const prixPublicCentimes = oeuvre.prixPublicCentimes;
  let prixProposeCentimes = prixPublicCentimes;
  if (corps.prixProposeCentimes !== undefined) prixProposeCentimes = corps.prixProposeCentimes;
  if (corps.remiseCentimes !== undefined) prixProposeCentimes -= corps.remiseCentimes;
  if (corps.remisePointsDeBase !== undefined)
    prixProposeCentimes = Math.round(prixPublicCentimes * (1 - corps.remisePointsDeBase / 10000));
  const remiseCentimes = prixPublicCentimes - prixProposeCentimes;
  const remisePointsDeBase = Math.round((remiseCentimes * 10000) / prixPublicCentimes);
  const remiseMax =
    reglages.parArtiste.find((artiste) => artiste.artisteId === oeuvre.artisteId)
      ?.remiseMaxPointsDeBase ?? reglages.remiseMaxPointsDeBase;
  const plancherEffectifCentimes =
    planchers.get(oeuvre.id) ?? Math.round(prixPublicCentimes * (1 - remiseMax / 10000));
  // La démonstration reprend le calcul fiscal partagé, avec le profil de Sainte-Avoye.
  const facture = calculerFacture({
    lignes: [
      {
        designation: oeuvre.titre,
        prixUnitaireCentimes: prixProposeCentimes,
        regime: oeuvre.regimeTva,
        ...(oeuvre.prixAchatCentimes == null
          ? {}
          : { prixAchatCentimes: oeuvre.prixAchatCentimes }),
        droitDeSuite: oeuvre.droitDeSuite,
      },
    ],
  });
  const netArtisteCentimes = oeuvre.prixArtisteCentimes ?? null;
  const accord =
    approbations.find(
      (accord) =>
        accord.statut === 'acceptee' &&
        accord.contactId === corps.contactId &&
        accord.oeuvreId === corps.oeuvreId &&
        accord.exemplaireId === (corps.exemplaireId ?? null) &&
        accord.prixProposeCentimes === prixProposeCentimes &&
        accord.expireLe !== null &&
        accord.expireLe > maintenant(),
    ) ?? null;
  return {
    prixPublicCentimes,
    prixProposeCentimes,
    remiseCentimes,
    remisePointsDeBase,
    totalHtCentimes: facture.totalHtCentimes,
    totalTvaCentimes: facture.totalTvaCentimes,
    totalTtcCentimes: facture.totalTtcCentimes,
    tvaSurMargeCentimes: facture.tvaSurMargeCentimes,
    droitDeSuiteCentimes: facture.droitDeSuiteCentimes,
    droitDeSuiteAChargeAcheteur: false,
    regimeEffectif: oeuvre.regimeTva,
    netArtisteCentimes,
    netGalerieCentimes:
      netArtisteCentimes === null
        ? null
        : facture.totalHtCentimes -
          facture.tvaSurMargeCentimes -
          netArtisteCentimes -
          facture.droitDeSuiteCentimes,
    plancherEffectifCentimes,
    sousPlancher: prixProposeCentimes < plancherEffectifCentimes,
    approbationRequise:
      reglages.seuilApprobationPointsDeBase !== null &&
      remisePointsDeBase > reglages.seuilApprobationPointsDeBase,
    approbation: accord,
  };
}
const presenterAccord = (accord: ApprobationNegociation): ApprobationNegociation => ({
  ...accord,
  statut:
    accord.statut === 'acceptee' && accord.expireLe !== null && accord.expireLe <= maintenant()
      ? 'expiree'
      : accord.statut,
});

export function reponseDemoPresentation(
  chemin: string,
  params: URLSearchParams,
  methode: string,
  corps?: unknown,
): unknown | undefined {
  if (chemin === '/selections') {
    if (methode === 'POST') return { selection: structuredClone(creer(corps as CorpsSelection)) };
    if (methode === 'GET') {
      const resultat = liste(
        selections.filter(
          (selection) =>
            (!params.has('archivee') ||
              selection.archivee === (params.get('archivee') === 'true')) &&
            (!params.has('contactId') || selection.contactId === params.get('contactId')),
        ),
        params,
      );
      return { selections: structuredClone(resultat.elements), pagination: resultat.pagination };
    }
  }
  const routeSelection = /^\/selections\/([^/]+)(.*)$/.exec(chemin);
  if (routeSelection) {
    const selection = selections.find((selection) => selection.id === routeSelection[1]);
    if (!selection) throw new ApiError(404, 'introuvable', 'Sélection introuvable.');
    const suite = routeSelection[2];
    if (!suite && methode === 'GET') return { selection: structuredClone(selection) };
    if (!suite && methode === 'PATCH') {
      const modification = corps as ModificationSelection;
      if (modification.version !== selection.version) conflit();
      const { version: _version, ...champs } = modification;
      Object.assign(selection, champs, {
        version: selection.version + 1,
        modifieeLe: maintenant(),
      });
      return { selection: structuredClone(selection) };
    }
    if (suite === '/elements' && methode === 'PUT') {
      const remplacement = corps as { elements: ElementSelection[]; version: number };
      if (remplacement.version !== selection.version) conflit();
      selection.elements = structuredClone(remplacement.elements);
      selection.version++;
      return { selection: structuredClone(selection) };
    }
    if (suite === '/duplication' && methode === 'POST') {
      const copie = creer({
        titre: `${selection.titre} (copie)`,
        note: selection.note,
        contactId: selection.contactId,
        elements: selection.elements,
      });
      copie.origine = { type: 'duplication', id: selection.id };
      return { selection: structuredClone(copie) };
    }
    if (suite === '/archivage' && (methode === 'POST' || methode === 'DELETE')) {
      selection.archivee = methode === 'POST';
      selection.archiveeLe = selection.archivee ? maintenant() : null;
      selection.version++;
      return { selection: structuredClone(selection) };
    }
    if (suite === '/presentation' && methode === 'GET') {
      const charge: ChargePresentation = {
        selection: structuredClone(selection),
        elements: selection.elements.map((element) => {
          const oeuvre = DEMO_OEUVRES.find((oeuvre) => oeuvre.id === element.oeuvreId);
          if (!oeuvre) throw new ApiError(404, 'introuvable', 'Œuvre introuvable.');
          return {
            ...element,
            oeuvre: structuredClone(oeuvre),
            exemplaire: null,
            artiste: { id: oeuvre.artisteId, nom: oeuvre.artisteNom },
            imageUrl: DEMO_VIGNETTES[oeuvre.id] ?? null,
            plancherCentimes: planchers.get(oeuvre.id) ?? null,
          };
        }),
        interets: structuredClone(interets.get(selection.id) ?? []),
      };
      return charge;
    }
    const interetRoute = /^\/interets\/([^/]+)$/.exec(suite ?? '');
    if (interetRoute && methode === 'PUT') {
      const entree = corps as { exemplaireId?: string | null; interesse: boolean };
      const interet: InteretPresentation = {
        oeuvreId: interetRoute[1]!,
        exemplaireId: entree.exemplaireId ?? null,
        interesse: entree.interesse,
        marqueLe: maintenant(),
      };
      const precedents = interets.get(selection.id) ?? [];
      interets.set(selection.id, [
        ...precedents.filter(
          (precedent) =>
            precedent.oeuvreId !== interet.oeuvreId ||
            precedent.exemplaireId !== interet.exemplaireId,
        ),
        interet,
      ]);
      selection.version++;
      return { interet: structuredClone(interet) };
    }
    if (suite === '/recapitulatif' && methode === 'GET')
      return {
        selection: structuredClone(selection),
        interets: structuredClone(
          (interets.get(selection.id) ?? []).filter((interet) => interet.interesse),
        ),
        actions: {
          notes: selection.contactId !== null,
          offre: true,
          affaire: false,
          reservation: true,
        },
      };
    if (suite === '/recapitulatif/notes' && methode === 'POST') {
      const contact = DEMO_CONTACTS.find((contact) => contact.id === selection.contactId);
      if (!contact) throw new ApiError(400, 'validation.invalide', 'Contact requis.');
      const titres = (interets.get(selection.id) ?? [])
        .filter((interet) => interet.interesse)
        .map((interet) => DEMO_OEUVRES.find((oeuvre) => oeuvre.id === interet.oeuvreId)?.titre)
        .filter(Boolean)
        .join(', ');
      DEMO_CONTACTS[DEMO_CONTACTS.indexOf(contact)] = {
        ...contact,
        notes: `${contact.notes}\n[${maintenant()}] ${selection.titre} — ${titres}. ${(corps as { note?: string }).note ?? ''}`,
      };
      return { contactId: contact.id };
    }
  }
  if (chemin === '/negociation/reglages') {
    if (methode === 'GET') return { reglages: structuredClone(reglages) };
    if (methode === 'PUT') {
      const entree = corps as ReglagesNegociation;
      if (entree.version !== reglages.version) conflit();
      reglages = { ...structuredClone(entree), version: reglages.version + 1 };
      return { reglages: structuredClone(reglages) };
    }
  }
  const plancher = /^\/oeuvres\/([^/]+)\/plancher$/.exec(chemin);
  if (plancher) {
    const id = plancher[1]!;
    if (methode === 'PUT')
      planchers.set(id, (corps as { plancherCentimes: number }).plancherCentimes);
    if (methode === 'DELETE') planchers.delete(id);
    return { plancherCentimes: planchers.get(id) ?? null };
  }
  if (chemin === '/negociation/calcul' && methode === 'POST')
    return { calcul: calculer(corps as EntreeCalcul) };
  if (chemin === '/negociation/approbations/compte' && methode === 'GET')
    return { compte: approbations.filter((accord) => accord.statut === 'en_attente').length };
  if (chemin === '/negociation/approbations') {
    if (methode === 'GET') {
      const resultat = liste(
        approbations
          .map(presenterAccord)
          .filter(
            (accord) =>
              (!params.has('statut') || accord.statut === params.get('statut')) &&
              (!params.has('contactId') || accord.contactId === params.get('contactId')) &&
              (!params.has('oeuvreId') || accord.oeuvreId === params.get('oeuvreId')) &&
              (!params.has('valides') || accord.statut === 'acceptee'),
          ),
        params,
      );
      return { approbations: structuredClone(resultat.elements), pagination: resultat.pagination };
    }
    if (methode === 'POST') {
      const demande = corps as DemandeApprobation;
      const calcul = calculer(demande);
      if (!calcul.approbationRequise)
        throw new ApiError(400, 'validation.invalide', 'Approbation non requise.');
      const accord: ApprobationNegociation = {
        id: `approbation-demo-${++numero}`,
        oeuvreId: demande.oeuvreId,
        exemplaireId: demande.exemplaireId ?? null,
        contactId: demande.contactId,
        selectionId: demande.selectionId ?? null,
        prixPublicCentimes: calcul.prixPublicCentimes,
        prixProposeCentimes: calcul.prixProposeCentimes,
        remisePointsDeBase: calcul.remisePointsDeBase,
        demandeur: {
          compteId: 'demo-compte-1',
          jetonId: null,
          email: 'direction@sainte-avoye.example',
        },
        demandeeLe: maintenant(),
        motifDemande: demande.motifDemande ?? '',
        statut: 'en_attente',
        decision: null,
        expireLe: null,
      };
      approbations.unshift(accord);
      return { approbation: structuredClone(accord) };
    }
  }
  const decision = /^\/negociation\/approbations\/([^/]+)\/decision$/.exec(chemin);
  if (decision && methode === 'POST') {
    const accord = approbations.find((accord) => accord.id === decision[1]);
    if (!accord) throw new ApiError(404, 'introuvable', 'Approbation introuvable.');
    if (accord.demandeur.compteId === 'demo-compte-1')
      throw new ApiError(403, 'acces.refuse', 'Vous ne pouvez pas décider votre propre demande.');
    if (accord.statut !== 'en_attente') conflit();
    const entree = corps as { decision: 'acceptee' | 'refusee'; note: string };
    if (entree.decision === 'refusee' && !entree.note.trim())
      throw new ApiError(400, 'validation.invalide', 'Motivez le refus.');
    accord.statut = entree.decision;
    accord.decision = { par: 'compte-demo', jetonId: null, le: maintenant(), note: entree.note };
    accord.expireLe = new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString();
    return { approbation: structuredClone(accord) };
  }
  return undefined;
}

const oeuvreInitiale = DEMO_OEUVRES[0]!;
const selectionInitiale = creer({
  titre: i18n.t('presentation:demo.selection'),
  contactId: DEMO_CONTACTS[0]!.id,
  elements: DEMO_OEUVRES.slice(0, 3).map((oeuvre) => ({ oeuvreId: oeuvre.id })),
});
approbations.push({
  id: 'approbation-demo-externe',
  oeuvreId: oeuvreInitiale.id,
  exemplaireId: null,
  contactId: DEMO_CONTACTS[0]!.id,
  selectionId: selectionInitiale.id,
  prixPublicCentimes: oeuvreInitiale.prixPublicCentimes!,
  prixProposeCentimes: 1000000,
  remisePointsDeBase: 2000,
  demandeur: { compteId: 'vendeur-demo', jetonId: null, email: 'vendeur@sainte-avoye.example' },
  demandeeLe: maintenant(),
  motifDemande: '',
  statut: 'en_attente',
  decision: null,
  expireLe: null,
});
